import { useParams } from 'react-router';
import { useCallback } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { useSimpleWritePattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleWritePattern';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { AdminLayout } from 'apps/sigma2-ui/src/components/AdminLayout';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import React from 'react';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { RubricBankItem } from 'libs/end-to-end/src/types/rubric-bank/rubric-bank-item';
import { RubricBankItemForWrite } from 'libs/end-to-end/src/types/rubric-bank/rubric-bank-item';
import { RubricBankItemCriteria } from 'libs/end-to-end/src/types/rubric-bank/rubric-bank-item-criteria';
import { useNavigateSafe } from 'apps/sigma2-ui/src/lib/hooks/useNavigateSafe';
import { AdminBreadcrumbs } from 'apps/sigma2-ui/src/components/ui/AdminBreadcrumbs';

export const DatabaseRubricBankItemPage = () => {
  const { organizationId, databaseId, itemId } = useParams();

  const navigate = useNavigateSafe();

  if (!organizationId || !databaseId || !itemId) {
    throw new Error(
      'DatabaseRubricBankPage: organizationId or databaseId or constructId is not defined',
    );
  }

  const [item, setItem] = useState<RubricBankItem | null>(null);

  const getUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/rubric-bank-items/${itemId}`;
  }, [organizationId, databaseId, itemId]);

  const readItemPattern = useSimpleReadPattern({
    getUrlFn,
    canRead: true,
    responseSchema: RubricBankItem,
  });

  const updateItemPattern = useSimpleWritePattern({
    getUrlFn,
    method: 'PUT',
    canWrite: true,
    writeSchema: RubricBankItemForWrite,
    responseSchema: EmptyObject,
  });

  const deleteConstructPattern = useSimpleWritePattern({
    getUrlFn,
    method: 'DELETE',
    canWrite: true,
    writeSchema: EmptyObject,
    responseSchema: EmptyObject,
  });

  const onDelete = useCallback(() => {
    if (deleteConstructPattern.status !== 'saving') {
      deleteConstructPattern.saveEntity({}, () => {
        navigate(
          `/organizations/${organizationId}/databases/${databaseId}/rubric-bank-items`,
        );
      });
    }
  }, [deleteConstructPattern, navigate, organizationId, databaseId]);

  const setRubricCriterias = useCallback(
    (criterias: RubricBankItemCriteria[]) => {
      if (item) {
        setItem({
          ...item,
          criteria: criterias,
        });
      }
    },
    [item, setItem],
  );

  useEffect(() => {
    if (readItemPattern.status === 'done') {
      setItem(readItemPattern.entity);
    }
  }, [readItemPattern]);

  const itemCriteriasPayload = useCrudOperations({
    items: item ? item.criteria : [],
    setItems: setRubricCriterias,
    postProcessNewItems: (newItems) => {
      return newItems.map((item, index) => {
        return {
          ...item,
          id: `${index}`,
        };
      });
    },
  });

  const isLoading = useMemo(() => {
    return (
      readItemPattern.status === 'loading' ||
      updateItemPattern.status === 'saving'
    );
  }, [readItemPattern, updateItemPattern]);

  return (
    <AdminLayout currentPage={'DatabaseRubricBankItem'}>
      <AdminBreadcrumbs
        items={[
          { label: 'Home', to: '/admin' },
          { label: 'Organizations', to: '/organizations' },
          {
            label: 'Databases',
            to: `/organizations/${organizationId}/databases`,
          },
          {
            label: 'Edit Database',
            to: `/organizations/${organizationId}/databases/edit/${databaseId}`,
          },
          {
            label: 'Rubric Bank',
            to: `/organizations/${organizationId}/databases/${databaseId}/rubric-bank`,
          },
          {
            label: 'Rubric Bank Items',
            to: `/organizations/${organizationId}/databases/${databaseId}/rubric-bank-items`,
          },
          {
            label: 'Rubric Bank Item',
            to: `/organizations/${organizationId}/databases/${databaseId}/rubric-bank-items/${itemId}`,
          },
        ]}
      />
      <Spacing size={'md'} direction={'vertical'}>
        {item && (
          <Spacing size={'md'} direction={'vertical'}>
            <input
              className="form-input"
              type={'text'}
              value={item.name}
              onChange={(e) => {
                if (item) {
                  setItem({
                    ...item,
                    name: e.target.value,
                  });
                }
              }}
            />

            <Button
              behavior={{
                type: 'onClick',
                onClick: () => {
                  itemCriteriasPayload.onAdd({
                    id: '2',
                    description: '',
                    weight: 1,
                  });
                },
              }}
              variant={'primary'}
              size={'small'}
              text={'Add Criteria'}
            />

            <Table
              columnDefinitions={[
                {
                  id: 'id',
                  header: () => 'Id',
                  cell: ({ item }) => <div>{item.id}</div>,
                },
                {
                  id: 'description',
                  header: () => 'Description',
                  cell: ({ item, rowIndex }) => (
                    <textarea
                      className="form-textarea"
                      style={{
                        width: '100%',
                      }}
                      value={item.description}
                      onChange={(event) => {
                        itemCriteriasPayload.onEdit(rowIndex, {
                          ...item,
                          description: event.target.value,
                        });
                      }}
                    />
                  ),
                },
                {
                  id: 'weight',
                  header: () => 'Weight',
                  cell: ({ item, rowIndex }) => (
                    <input
                      className="form-input"
                      type="number"
                      style={{
                        width: '100%',
                      }}
                      key={`weight_${rowIndex}`}
                      value={item.weight}
                      aria-label={`weight_${rowIndex}`}
                      onChange={(event) => {
                        itemCriteriasPayload.onEdit(rowIndex, {
                          ...item,
                          weight: Number(event.target.value),
                        });
                      }}
                    />
                  ),
                },
                {
                  id: 'modify',
                  header: () => 'Delete',
                  cell: ({ rowIndex }) => (
                    <Button
                      behavior={{
                        type: 'onClick',
                        onClick: () => {
                          itemCriteriasPayload.onDelete(rowIndex);
                        },
                      }}
                      variant={'outline'}
                      size={'small'}
                      text={'Delete'}
                    />
                  ),
                },
              ]}
              items={item.criteria}
              isLoading={isLoading}
            />
          </Spacing>
        )}
        <Spacing size={'justify-between'} direction={'horizontal'}>
          <Button
            behavior={{
              type: 'onClick',
              onClick: () => {
                if (updateItemPattern.status !== 'saving' && item) {
                  updateItemPattern.saveEntity(item);
                }
              },
            }}
            variant={'primary'}
            size={'small'}
            text={'Save'}
          />

          <Button
            behavior={{
              type: 'onClick',
              onClick: () => {
                onDelete();
              },
            }}
            variant={'destructive'}
            size={'small'}
            text={'Delete Rubric Item'}
          />
        </Spacing>
      </Spacing>
    </AdminLayout>
  );
};
